import { zodResolver } from '@hookform/resolvers/zod'
import clsx from 'clsx'
import { TFunction } from 'i18next'
import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import Button from '@/components-latest/primitives/Button'
import ChipSelect from '@/components-latest/primitives/Controls/ChipSelect'
import Input from '@/components-latest/primitives/Controls/Input'
import PhoneInput from '@/components-latest/primitives/Controls/PhoneInput'
import PlaceInput from '@/components-latest/primitives/Controls/PlacesInput'
import {
  EmptyMessage,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components-latest/primitives/Controls/Select'
import Label from '@/components-latest/primitives/Label'
import Text, { textVariants } from '@/components-latest/primitives/Text'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage
} from '@/components-latest/shared/Form'
import GoogleMapComponent from '@/components-latest/ui/RegistrationPage/GoogleMapComponent'
import i18n from '@/config/i18n'
import { CoordsSchema } from '@/ducks/common.schema'
import { Coords } from '@/ducks/common.types'
import { useLazyFetchCuisinesBySubCityQuery } from '@/ducks/registration/api/registration.api'
import {
  ApiRegistrationError,
  City,
  Country,
  Cuisine
} from '@/ducks/registration/registration.types'
import { useDeviceSize } from '@/hooks/useDeviceSize/useDeviceSize'

import Styles from './RegistrationForm.module.scss'
import { phoneSchema } from '@/components-latest/primitives/Controls/PhoneInput/phoneSchema'

const INITIAL_MAP_CENTER = { lat: 40.74, lng: -74.18 }
const SERVER_PARAM_TO_FIELD = {
  resname: 'restaurantName',
  main_city: 'citySelect',
  cpwd: 'confirmPassword',
  line1: 'address.fullAddress',
  city: 'address.city',
  state: 'address.state',
  country: 'address.country',
  countryCode: 'address.countryCode',
  lat: 'address.coords.lat',
  lng: 'address.coords.lng',
  username: 'username',
  email: 'email',
  'phone.code': 'phone',
  'phone.number': 'phone',
  main_cuisine: 'cuisines'
} as const

function containsWordCaseInsensitive(str: string, word: string): boolean {
  if (!str) return false

  const lowerStr = str.toLowerCase()
  const lowerWord = word.toLowerCase()

  const regex = new RegExp(`\\b${lowerWord}\\b`)

  return regex.test(lowerStr)
}

const formSchemaCreator = (t: TFunction) =>
    z.object({
      countrySelect: z
        .string()
        .min(1, i18n.t('registration.countrySelect.required')),
      citySelect: z.string().min(1, i18n.t('registration.citySelect.required')),
      restaurantName: z
        .string()
        .min(1, i18n.t('registration.restaurantName.required'))
        .regex(
          /^[a-zA-Z0-9_. ]*$/,
          i18n.t('registration.restaurantName.invalidPattern')
        )
        .min(4, i18n.t('common.errors.minLength', { count: 4 }))
        .max(30, i18n.t('common.errors.maxLength', { count: 30 })),
      username: z
        .string()
        .min(1, i18n.t('registration.username.required'))
        .max(20, i18n.t('common.errors.maxLength', { count: 20 }))
        .regex(/^[a-zA-Z ]*$/, i18n.t('registration.username.invalidPattern')),
      cuisines: z
        .array(
          z.object({
            value: z.string(),
            label: z.string()
          })
        )
        .superRefine((val, ctx) => {
          if (val && val.length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: i18n.t('registration.cuisines.required')
            })
          }
        }),
      email: z
        .string()
        .min(1, i18n.t('registration.email.required'))
        .email(i18n.t('registration.email.invalidPattern'))
        .min(6, i18n.t('common.errors.minLength', { count: 6 }))
        .max(40, i18n.t('common.errors.maxLength', { count: 40 })),
      phone: phoneSchema,
      password: z
        .string()
        .min(1, i18n.t('registration.password.required'))
        .min(6, i18n.t('common.errors.minLength', { count: 6 }))
        .max(20, i18n.t('common.errors.maxLength', { count: 6 }))
        .regex(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
          i18n.t('registration.password.invalidPattern')
        ),
      confirmPassword: z
        .string()
        .min(1, i18n.t('registration.confirmPassword.required')),
      address: z.object({
        fullAddress: z
          .string()
          .min(1, i18n.t('registration.fullAddress.required'))
          .min(4, i18n.t('common.errors.minLength', { count: 4 })),
        coords: CoordsSchema,
        city: z.string().min(1, i18n.t('registration.city.required')),
        state: z.string().min(1, i18n.t('registration.state.required')),
        country: z.string().min(1, i18n.t('registration.country.required')),
        countryCode: z
          .string()
          .min(1, i18n.t('registration.countryCode.required'))
      })
    })
    .superRefine((data, ctx) => {
      if (data.password !== data.confirmPassword) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('registration.confirmPassword.mismatch'),
          path: ['confirmPassword']
        })
      }
    })
type RegistrationFormData = z.infer<ReturnType<typeof formSchemaCreator>>

interface IRegistrationFormProps {
  countries: Country[]
  cities: City[]
  cuisines: Cuisine[]
  onSubmit: (data: RegistrationFormData) => void
  isSubmitting: boolean
  error?: ApiRegistrationError
  initialValues?: RegistrationFormData
}

const RegistrationForm: React.FC<IRegistrationFormProps> = ({
  countries = [],
  cities = [],
  cuisines = [],
  isSubmitting = false,
  onSubmit,
  error,
  initialValues
}) => {
  const { t, i18n } = useTranslation()
  const [mapCenter, setMapCenter] = useState<Coords>(INITIAL_MAP_CENTER)
  const { isMobile } = useDeviceSize()

  const [fetchCuisinesBySubCity, fetchCuisinesBySubCityQuery] =
    useLazyFetchCuisinesBySubCityQuery()

  const citiesByCountryCode = useMemo(() => {
    const citiesByCountryCode: Record<string, City[]> = {}
    cities.forEach(city => {
      const { country_code } = city
      if (country_code) {
        if (!citiesByCountryCode[country_code]) {
          citiesByCountryCode[country_code] = []
        }
        citiesByCountryCode[country_code].push(city)
      }
    })
    return citiesByCountryCode
  }, [cities])

  const formSchema = useMemo(() => formSchemaCreator(t), [t])

  const baseDefaultValues = useMemo(
    () => ({
      countrySelect: '',
      citySelect: '',
      restaurantName: '',
      username: '',
      cuisines: [],
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      address: {
        fullAddress: '',
        coords: { lat: 0, lng: 0 },
        city: '',
        state: '',
        country: '',
        countryCode: ''
      }
    }),
    []
  )

  const mergedDefaultValues = useMemo(() => {
    if (!initialValues) return baseDefaultValues

    return {
      ...baseDefaultValues,
      ...initialValues,
      address: {
        ...baseDefaultValues.address,
        ...initialValues.address,
        coords: {
          ...baseDefaultValues.address.coords,
          ...initialValues.address?.coords
        }
      }
    }
  }, [baseDefaultValues, initialValues])

  const form = useForm<RegistrationFormData>({
    resolver: zodResolver(formSchema),
    mode: 'onTouched',
    defaultValues: mergedDefaultValues
  })
  const {
    trigger,
    setValue,
    setError,
    watch,
    formState: { errors }
  } = form

  const countrySelect = watch('countrySelect')
  useEffect(
    function clearSubCity() {
      setValue('citySelect', '')
    },
    [countrySelect]
  )

  const citySelect = watch('citySelect')
  useEffect(
    function updateCuisinesOptionsByCity() {
      setValue('cuisines', [])
      fetchCuisinesBySubCity(citySelect)
    },
    [fetchCuisinesBySubCity, citySelect]
  )
  useEffect(
    function updateMapCenterByCity() {
      const location = cities.find(
        city => city.cityname === citySelect
      )?.location

      if (location) {
        setMapCenter(location)
      }
    },
    [citySelect, cities]
  )

  const cuisineOptions = React.useMemo(() => {
    const composeOptions = (cuisine: Cuisine) => ({
      value: cuisine._id,
      label: cuisine.name
    })

    return (fetchCuisinesBySubCityQuery.data || cuisines)?.map(composeOptions)
  }, [cuisines, fetchCuisinesBySubCityQuery.data])

  useEffect(() => {
    if (error && 'errors' in error) {
      error?.errors?.forEach(error => {
        if (error.param in SERVER_PARAM_TO_FIELD) {
          setError(
            SERVER_PARAM_TO_FIELD[
              error.param as keyof typeof SERVER_PARAM_TO_FIELD
            ],
            {
              message: error.msg
            }
          )
        } else {
          setError('root.serverError', {
            message: error.msg
          })
        }
      })
    } else if (error && 'message' in error) {
      if (containsWordCaseInsensitive(error?.message, 'email')) {
        setError('email', {
          message: error?.message || ''
        })
      }
      if (containsWordCaseInsensitive(error?.message, 'phone')) {
        setError('phone', {
          message: error?.message || ''
        })
      }
    }
  }, [error])

  useEffect(() => {
    if (!initialValues) return

    form.reset(mergedDefaultValues)
    if (initialValues.address?.coords) {
      setMapCenter(initialValues.address.coords)
    }
  }, [form, initialValues, mergedDefaultValues])

  useEffect(
    function translateErrorMessages() {
      if (Object.keys(errors).length > 0) {
        const fieldsToValidate = Object.keys(
          errors
        ) as (keyof RegistrationFormData)[]
        trigger(fieldsToValidate)
      }
    },
    [errors, i18n.language, trigger]
  )

  return (
    <Form {...form}>
      <form
        className={Styles.form}
        onSubmit={form.handleSubmit(onSubmit)}
        id="registration">
        <div className={Styles.fieldsetsWrapper}>
          <fieldset className={Styles.fieldset}>
            <FormField
              name="countrySelect"
              control={form.control}
              render={({ field }) => {
                return (
                  <FormItem>
                    <Label layout="column">
                      <Text
                        variant="nextSubtitle"
                        weight="bold"
                        className={Styles.label}>
                        {t('registration.countrySelect.label')}
                      </Text>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}>
                          <SelectTrigger
                            className={Styles.selectTrigger}
                            onBlur={field.onBlur}
                            variant="input"
                            ref={field.ref}>
                            <SelectValue
                              placeholder={t(
                                'registration.countrySelect.placeholder'
                              )}
                              className={clsx(
                                textVariants({ variant: 'nextBody' }),
                                Styles.selectValue
                              )}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {countries.map(country => (
                              <SelectItem
                                key={country.code}
                                value={country.code}>
                                {country.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </Label>
                    <div className={Styles.errorWrapper}>
                      <FormMessage className={Styles.errorMessage} />
                    </div>
                  </FormItem>
                )
              }}
            />
            <FormField
              name="citySelect"
              control={form.control}
              render={({ field }) => {
                return (
                  <FormItem>
                    <Label layout="column">
                      <Text
                        variant="nextSubtitle"
                        weight="bold"
                        className={Styles.label}>
                        {t('registration.citySelect.label')}
                      </Text>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}>
                          <SelectTrigger
                            className={Styles.selectTrigger}
                            onBlur={field.onBlur}
                            variant="input"
                            ref={field.ref}>
                            <SelectValue
                              placeholder={t(
                                'registration.citySelect.placeholder'
                              )}
                              className={clsx(
                                textVariants({ variant: 'nextBody' }),
                                Styles.selectValue
                              )}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {citiesByCountryCode[countrySelect]?.length > 0 ? (
                              citiesByCountryCode[countrySelect].map(
                                country => (
                                  <SelectItem
                                    key={country.cityname}
                                    value={country.cityname}>
                                    {country.cityname}
                                  </SelectItem>
                                )
                              )
                            ) : (
                              <EmptyMessage>
                                {t('registration.citySelect.noCity')}
                              </EmptyMessage>
                            )}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </Label>
                    <div className={Styles.errorWrapper}>
                      <FormMessage className={Styles.errorMessage} />
                    </div>
                  </FormItem>
                )
              }}
            />
            <FormField
              name="restaurantName"
              control={form.control}
              render={({ field }) => {
                return (
                  <FormItem>
                    <Label layout="column">
                      <Text
                        variant="nextSubtitle"
                        weight="bold"
                        className={Styles.label}>
                        {t('registration.restaurantName.label')}
                      </Text>
                      <FormControl>
                        <Input
                          className={Styles.input}
                          placeholder={t(
                            'registration.restaurantName.placeholder'
                          )}
                          {...field}
                        />
                      </FormControl>
                    </Label>
                    <div className={Styles.errorWrapper}>
                      <FormMessage className={Styles.errorMessage} />
                    </div>
                  </FormItem>
                )
              }}
            />
            <FormField
              name="username"
              control={form.control}
              render={({ field }) => {
                return (
                  <FormItem>
                    <Label layout="column">
                      <Text
                        variant="nextSubtitle"
                        weight="bold"
                        className={Styles.label}>
                        {t('registration.username.label')}
                      </Text>
                      <FormControl>
                        <Input
                          className={Styles.input}
                          placeholder={t('registration.username.placeholder')}
                          {...field}
                        />
                      </FormControl>
                    </Label>
                    <div className={Styles.errorWrapper}>
                      <FormMessage className={Styles.errorMessage} />
                    </div>
                  </FormItem>
                )
              }}
            />
            <FormField
              name="cuisines"
              control={form.control}
              render={({ field }) => {
                return (
                  <FormItem className={Styles.cuisines}>
                    <Label layout="column">
                      <Text
                        variant="nextSubtitle"
                        weight="bold"
                        className={Styles.label}>
                        {t('registration.cuisines.label')}
                      </Text>
                      <FormControl>
                        <ChipSelect
                          {...field}
                          isLoading={fetchCuisinesBySubCityQuery.isFetching}
                          options={cuisineOptions}
                          placeholder={t('registration.cuisines.placeholder')}
                          emptyOptionsMessage={t(
                            'registration.cuisines.empty-options-message'
                          )}
                        />
                      </FormControl>
                    </Label>
                    <div className={Styles.errorWrapper}>
                      <FormMessage className={Styles.errorMessage} />
                    </div>
                  </FormItem>
                )
              }}
            />
          </fieldset>

          <fieldset className={Styles.fieldset}>
            <FormField
              name="email"
              control={form.control}
              render={({ field }) => {
                return (
                  <FormItem className={Styles.email}>
                    <Label layout="column">
                      <Text
                        variant="nextSubtitle"
                        weight="bold"
                        className={Styles.label}>
                        {t('registration.email.label')}
                      </Text>
                      <FormControl>
                        <Input
                          type="email"
                          className={Styles.input}
                          placeholder={t('registration.email.placeholder')}
                          {...field}
                        />
                      </FormControl>
                    </Label>
                    <div className={Styles.errorWrapper}>
                      <FormMessage className={Styles.errorMessage} />
                    </div>
                  </FormItem>
                )
              }}
            />
            <FormField
              name="phone"
              control={form.control}
              render={({ field }) => {
                return (
                  <FormItem className={Styles.phone}>
                    <Label layout="column">
                      <Text
                        variant="nextSubtitle"
                        weight="bold"
                        className={Styles.label}>
                        {t('registration.phone.label')}
                      </Text>
                      <FormControl>
                        <PhoneInput
                          className={Styles.input}
                          placeholder={t('registration.phone.placeholder')}
                          zIndex={0}
                          {...field}
                        />
                      </FormControl>
                    </Label>
                    <div className={Styles.errorWrapper}>
                      <FormMessage className={Styles.errorMessage} />
                    </div>
                  </FormItem>
                )
              }}
            />
            <FormField
              name="password"
              control={form.control}
              render={({ field }) => {
                return (
                  <FormItem>
                    <Label layout="column">
                      <Text
                        variant="nextSubtitle"
                        weight="bold"
                        className={Styles.label}>
                        {t('registration.password.label')}
                      </Text>
                      <FormControl>
                        <Input
                          autoComplete="new-password"
                          type="password"
                          className={Styles.input}
                          placeholder={t('registration.password.placeholder')}
                          {...field}
                        />
                      </FormControl>
                    </Label>
                    <div className={Styles.errorWrapper}>
                      <FormMessage className={Styles.errorMessage} />
                    </div>
                  </FormItem>
                )
              }}
            />
            <FormField
              name="confirmPassword"
              control={form.control}
              render={({ field }) => {
                return (
                  <FormItem>
                    <Label layout="column">
                      <Text
                        variant="nextSubtitle"
                        weight="bold"
                        className={Styles.label}>
                        {t('registration.confirmPassword.label')}
                      </Text>
                      <FormControl>
                        <Input
                          autoComplete="new-password"
                          type="password"
                          className={Styles.input}
                          placeholder={t(
                            'registration.confirmPassword.placeholder'
                          )}
                          {...field}
                        />
                      </FormControl>
                    </Label>
                    <div className={Styles.errorWrapper}>
                      <FormMessage className={Styles.errorMessage} />
                    </div>
                  </FormItem>
                )
              }}
            />
          </fieldset>
          <fieldset className={Styles.fieldset}>
            <FormField
              name="address.fullAddress"
              control={form.control}
              render={({ field }) => {
                return (
                  <FormItem className={Styles.fullAddress}>
                    <Label layout="column">
                      <Text
                        variant="nextSubtitle"
                        weight="bold"
                        className={Styles.label}>
                        {t('registration.fullAddress.label')}
                      </Text>
                      <FormControl>
                        <PlaceInput
                          {...field}
                          placeholder={t(
                            'registration.fullAddress.placeholder'
                          )}
                          onSelectAddress={(_, details) => {
                            setValue('address.coords', details.coords)
                            setValue('address.city', details.city)
                            setValue('address.state', details.state)
                            setValue('address.country', details.country)
                            setValue('address.countryCode', details.countryCode)
                            setMapCenter(details.coords)
                          }}
                        />
                      </FormControl>
                    </Label>
                    <div className={Styles.errorWrapper}>
                      <FormMessage className={Styles.errorMessage} />
                    </div>
                  </FormItem>
                )
              }}
            />
            <FormField
              name="address.city"
              control={form.control}
              render={({ field }) => {
                return (
                  <FormItem>
                    <Label layout="column">
                      <Text
                        variant="nextSubtitle"
                        weight="bold"
                        className={Styles.label}>
                        {t('registration.city.label')}
                      </Text>
                      <FormControl>
                        <Input
                          readOnly
                          className={Styles.input}
                          placeholder={t('registration.city.placeholder')}
                          {...field}
                        />
                      </FormControl>
                    </Label>
                    <div className={Styles.errorWrapper}>
                      <FormMessage className={Styles.errorMessage} />
                    </div>
                  </FormItem>
                )
              }}
            />
            <FormField
              name="address.state"
              control={form.control}
              render={({ field }) => {
                return (
                  <FormItem>
                    <Label layout="column">
                      <Text
                        variant="nextSubtitle"
                        weight="bold"
                        className={Styles.label}>
                        {t('registration.state.label')}
                      </Text>
                      <FormControl>
                        <Input
                          readOnly
                          className={Styles.input}
                          placeholder={t('registration.state.placeholder')}
                          {...field}
                        />
                      </FormControl>
                    </Label>
                    <div className={Styles.errorWrapper}>
                      <FormMessage className={Styles.errorMessage} />
                    </div>
                  </FormItem>
                )
              }}
            />
            <FormField
              name="address.country"
              control={form.control}
              render={({ field }) => {
                return (
                  <FormItem>
                    <Label layout="column">
                      <Text
                        variant="nextSubtitle"
                        weight="bold"
                        className={Styles.label}>
                        {t('registration.country.label')}
                      </Text>
                      <FormControl>
                        <Input
                          readOnly
                          className={Styles.input}
                          placeholder={t('registration.country.placeholder')}
                          {...field}
                        />
                      </FormControl>
                    </Label>
                    <div className={Styles.errorWrapper}>
                      <FormMessage className={Styles.errorMessage} />
                    </div>
                  </FormItem>
                )
              }}
            />
            <FormField
              name="address.countryCode"
              control={form.control}
              render={({ field }) => {
                return (
                  <FormItem>
                    <Label layout="column">
                      <Text
                        variant="nextSubtitle"
                        weight="bold"
                        className={Styles.label}>
                        {t('registration.countryCode.label')}
                      </Text>
                      <FormControl>
                        <Input
                          readOnly
                          className={Styles.input}
                          placeholder={t(
                            'registration.countryCode.placeholder'
                          )}
                          {...field}
                        />
                      </FormControl>
                    </Label>
                    <div className={Styles.errorWrapper}>
                      <FormMessage className={Styles.errorMessage} />
                    </div>
                  </FormItem>
                )
              }}
            />
            <FormField
              name="address.coords"
              control={form.control}
              render={({ field }) => {
                return (
                  <FormItem className={Styles.coords}>
                    <Label layout="column">
                      <FormControl>
                        <div className={Styles.mapWrapper}>
                          <GoogleMapComponent
                            {...field}
                            onMarkerPositionChange={(position: Coords) =>
                              field.onChange(position)
                            }
                            value={field.value}
                            center={mapCenter}
                          />
                        </div>
                      </FormControl>
                    </Label>
                    <div className={Styles.errorWrapper}>
                      <FormMessage className={Styles.errorMessage} />
                    </div>
                  </FormItem>
                )
              }}
            />
          </fieldset>
        </div>

        <div className={Styles.submitWrapper}>
          {form.formState.errors?.root?.serverError && (
            <Text
              variant="nextBody"
              className={Styles.serverMessage}
              gutter={12}>
              {form.formState.errors?.root?.serverError.message}
            </Text>
          )}
          <Button
            type="submit"
            variant="branded"
            size="large"
            loading={isSubmitting}>
            {isMobile ? t('registration.continue') : t('registration.submit')}
          </Button>
        </div>
      </form>
    </Form>
  )
}

export type { RegistrationFormData }

export default RegistrationForm
